import {HttpErrorResponse} from '@angular/common/http';
import {Component, ElementRef, HostListener, OnInit, QueryList, ViewChild, ViewChildren} from '@angular/core';
import {MatFormField} from '@angular/material/form-field';
import {MatPaginator} from '@angular/material/paginator';
import {MatSort} from '@angular/material/sort';
import {MatTableDataSource} from '@angular/material/table';
import {FabTableState, filterStateChange, process} from '@fabric/components/table-filter/data-query';
import {catchError, concat, forkJoin, map, Observable, ObservableInput, of, tap} from 'rxjs';
import {ErrorCodes} from '../../constants/ErrorCodes';
import {UserFunctionCode} from '../../constants/UserFunctionCode';
import {DeactivateGuardedComponent} from '../../interfaces/DeactivateGuardedComponent';
import {Almacen} from '../../models/Almacen';
import {PtoEmision} from '../../models/PtoEmision';
import {Solicitante} from '../../models/Solicitante';
import {AlmacenService} from '../../services/almacen.service';
import {NotificationService} from '../../services/notification.service';
import {PtoEmisionService} from '../../services/ptoEmision.service';
import {SecurityService} from '../../services/security.service';
import {SolicitanteService} from '../../services/solicitante.service';
import {UserService} from '../../services/user.service';
import {
  FabDateFilterOperator,
  RemDateFilterComponent
} from '../../shared-components/table-filter/date-filter/date-filter.component';
import {RemNumberFilterComponent} from '../../shared-components/table-filter/number-filter/number-filter.component';
import {
  FabStringFilterOperator,
  RemStringFilterComponent
} from '../../shared-components/table-filter/string-filter/string-filter.component';

class SolicitanteRow extends Solicitante {
  public add: boolean;
  public editing: boolean = false;
  public almacenField: string;

  private _originalSolicitante: Solicitante;

  public set originalSolicitante(solicitante: Solicitante) {
    this._originalSolicitante = solicitante;
    this.resetEdits();
  }

  public get originalSolicitante(): Solicitante {
    return this._originalSolicitante;
  }

  public get dockCode(): string {
    return this.almacen.dock?.dock ?? '';
  }

  public get dunsDock(): string {
    return this.almacen.dock?.dunsDock ?? '';
  }

  public changeAlmacen(almacen: Almacen) {
    this.almacen = almacen;
    this.almacenField = Almacen.combinedAlmacen(almacen);
  }

  constructor(add: boolean = false, solicitante?: Solicitante) {
    super();
    this._originalSolicitante = solicitante ?? new Solicitante();
    this.add = add;
    this.editing = add;
    this.resetEdits();
  }

  resetEdits() {
    const keys = Object.keys(new Solicitante()) as (keyof Solicitante)[];
    keys.forEach((key) => {
      switch (key) {
        case 'fecCreacion':
          this.fecCreacion = new Date(this._originalSolicitante.fecCreacion);
          break;
        case 'fecModif':
          this.fecModif = this._originalSolicitante.fecModif != null ? new Date(this._originalSolicitante.fecModif) : null;
          break;
        default:
          // @ts-ignore
          this[key] = this._originalSolicitante[key];
          break;
      }
    });
    this.almacenField = Almacen.combinedAlmacen(this.almacen);
  }
}

@Component({
  selector: 'app-solicitante-maintenance',
  templateUrl: './solicitante-maintenance.component.html',
  styleUrls: ['./solicitante-maintenance.component.scss']
})
export class SolicitanteMaintenanceComponent implements OnInit, DeactivateGuardedComponent {

  canCreateUpdate: boolean = false;
  isLoading: boolean = true;
  solicitantes: SolicitanteRow[] = [];
  almacenes: Almacen[] = [];
  almacenesMap: Map<string, Almacen> = new Map<string, Almacen>();
  ptoEmisions: PtoEmision[] = [];

  balanzaChoices: string[] = ['S', 'N'];

  displayedColumns: string[] = ['idSolicitante', 'solicitante', 'telefono', 'otrosDatos', 'mail', 'almacen', 'dock', 'dunsDock', 'ptoEmision', 'balanza', 'estado', 'usrCreacion', 'fecCreacion', 'usrModif', 'fecModif'];
  operators: FabStringFilterOperator[] = ['contains'];
  dateOperators: FabDateFilterOperator[] = ['eq', 'gte', 'lte'];
  pageSizeOptions: number[] = [10, 15, 20, 25];
  defaultPageSize: number = this.pageSizeOptions[0];
  dataSource: MatTableDataSource<SolicitanteRow> = new MatTableDataSource<SolicitanteRow>();

  state: FabTableState = {
    skip: 0,
    take: 10,
    filter: [],
    sort: []
  };

  selector: string;

  public get disableSaveButton() {
    return !this.validateAllRows() ||
      !this.solicitantes.filter(element => element.editing).map((row) => {
        const keys = Object.keys(new Solicitante()) as (keyof Solicitante)[];
        return keys.map((key) => {
          let dataChanged = this.isDirty(row, key);
          return dataChanged;
        }).some(item => item);
      }).some(item => item);
  }

  public filteredAlmacenes(element: SolicitanteRow) {
    return Array.from(this.almacenesMap.keys()).filter((item) => item.includes(element.almacenField));
  }

  constructor(private solicitanteService: SolicitanteService,
              private notificationService: NotificationService,
              private securityService: SecurityService,
              private almacenService: AlmacenService,
              private userService: UserService,
              private readonly ptoEmisionService: PtoEmisionService,
              private elementRef: ElementRef) {
  }

  ngOnInit(): void {
    this.canCreateUpdate = this.securityService.can(UserFunctionCode.MAINTENANCE);
    if (this.canCreateUpdate) {
      this.displayedColumns.push('edited');
    }
    this.fetchData();
    this.selector = this.elementRef.nativeElement.tagName.toLowerCase();
  }

  canExit(): Observable<boolean> | Promise<boolean> | boolean {
    if (this.dirtyCheck()) {
      return confirm("Usted tiene cambios no guardados. ¿Salir de la página de todos modos?");
    } else {
      return true;
    }
  }

  @HostListener('window:beforeunload', ['$event'])
  defaultConfirmation(event: any) {
    if (this.dirtyCheck()) {
      event.returnValue(false);
    } else {
      event.preventDefault();
    }
  }

  @ViewChild(MatSort) set matSort(sort: MatSort) {
    this.dataSource.sortingDataAccessor = (item, property) => {
      switch (property) {
        case 'dock':
          return item.almacen.dock?.dock;
        case 'almacen':
          return item.almacen.codAlmacen;
        case 'dockInactive':
          return item.almacen.dock?.estado ?? false;
        default:
          // @ts-ignore
          return item[property];
      }
    };
    this.dataSource.sort = sort;
  }

  @ViewChild(MatPaginator) set matPaginator(paginator: MatPaginator) {
    this.dataSource.paginator = paginator;
  }

  @ViewChildren('tableFilter') tableFilters: QueryList<RemStringFilterComponent | RemNumberFilterComponent | RemDateFilterComponent>;


  fetchData() {
    this.isLoading = true;
    let inactiveDockPresent = false;
    let solicitantes: Solicitante[];
    forkJoin([
      this.ptoEmisionService.getAllPtoEmision().pipe(
        tap((res) => {
          this.ptoEmisions = res;
        })
      ),
      this.almacenService.getAll().pipe(
        tap((res) => {
          this.almacenes = res;
          this.almacenes.forEach((item) => {
            this.almacenesMap.set(Almacen.combinedAlmacen(item), item);
          });
        })
      ),
      this.solicitanteService.getSolicitantes().pipe(
        tap((res) => {
          solicitantes = res;
        })
      )
    ]).subscribe({
      complete: () => {
        this.solicitantes = solicitantes.map((solicitante) => {
          solicitante.almacen = this.almacenes.find(
            (almacen) => almacen.codAlmacen === solicitante.almacen.codAlmacen) ?? solicitante.almacen;
          if (!solicitante.almacen.dock?.estado) {
            inactiveDockPresent = true;
          }
          return new SolicitanteRow(false, solicitante);
        });
        this.dataSource = new MatTableDataSource<SolicitanteRow>(this.solicitantes);
        this.sortTableById();
        this.processData();
        if (inactiveDockPresent) {
          this.displayedColumns.splice(this.displayedColumns.indexOf('dock') + 1, 0, 'dockInactive');
        }
        this.isLoading = false;
      }
    });
  }

  addRow() {
    let newRow = new SolicitanteRow(true);
    newRow.usrCreacion = this.securityService.LOGGED_USER['gmin'];
    this.solicitantes.unshift(newRow);
    this.processData();
  }

  saveRows() {
    if (this.validateAllRows()) {

      this.checkUsersExist().subscribe((existsResponse) => {
        if (existsResponse.every((item) => !item.exists) ||
          confirm(existsResponse.filter((item) => item.exists)
            .map((item) => item.name)
            .join(", ") + " está creado como usuario, por favor verifique.")
        ) {

          let inactiveObservables: ObservableInput<any>[] = [];
          let activeObservables: ObservableInput<any>[] = [];
          let errors: HttpErrorResponse[] = [];
          let saved = 0;
          this.solicitantes.filter((item) => item.editing)
            .forEach((solicitante) => {
                const keys = Object.keys(new Solicitante()) as (keyof Solicitante)[];
                let dirty = keys.map((key) => {
                  let test = this.isDirty(solicitante, key);
                  return test;
                }).some(item => item);
                if (dirty) {
                  let observableType = solicitante.estado ? activeObservables : inactiveObservables;
                  observableType.push(
                    this.solicitanteService.addUpdate(solicitante as Solicitante, solicitante.add)
                      .pipe(
                        map((res) => {
                          res.almacen = this.almacenes.find(
                            (almacen) => almacen.codAlmacen === solicitante.almacen.codAlmacen) ?? solicitante.almacen;
                          solicitante.originalSolicitante = res;
                          solicitante.editing = false;
                          solicitante.add = false;
                          saved++;
                        }),
                        catchError((err: HttpErrorResponse) => {
                          errors.push(err);
                          return of(null);
                        })
                      )
                  );
                }else{
                  this.revertRow(solicitante);
                }

              }
            );

          concat(forkJoin(inactiveObservables), forkJoin(activeObservables)).subscribe({
            next: undefined,
            error: undefined,
            complete: () => {
              if (errors.length == 0) {
                this.sortTableById();
                this.processData();
                this.notificationService.open(ErrorCodes.SAVED, 'success');
              } else {
                const errorCounts = errors.map(err => err.error?.exceptionType ?? "Unknown").reduce((accumulator, value) => {
                  // @ts-ignore
                  return {...accumulator, [value]: (accumulator[value] || 0) + 1};
                }, {});
                let errorMessage = `${saved} línea(s) guardada(s). ${errors.length} líena(s) tiene(n) error:`
                  .concat(...Object.entries(errorCounts).map(([msg, count]) => `\n${this.notificationService.getErrorByCode(msg)} (${count})`));
                this.notificationService.open(errorMessage, 'error');
              }
            }
          });

        } else {
          // user chose not to confirm after notification that users with same name exist
          this.notificationService.open(ErrorCodes.NOT_SAVED, 'warn');
        }

      });

    } else {
      // invalid rows error
      this.notificationService.open(ErrorCodes.INVALID_DATA, 'error');
    }
  }

  edit(row: SolicitanteRow) {
    if (!row.editing) {
      row.usrModif = this.securityService.LOGGED_USER['gmin'];
      row.editing = true;
    }
  }

  revertRow(row: SolicitanteRow) {
    if (row.add) {
      this.solicitantes = this.solicitantes.filter((item) => {
        return item !== row;
      })
    }
    row.resetEdits();
    row.editing = false;
    this.processData();
  }

  filterChange(event: any) {
    this.state.skip = 0;
    if (this.matPaginator) {
      this.matPaginator.pageIndex = 0;
    }
    // @ts-ignore
    this.state.filter = filterStateChange(event, this.state.filter);
    this.processData();
  }

  processData() {
    this.dataSource.data = process(this.solicitantes, this.state);
  }

  validateAllRows() {
    return this.solicitantes.filter(element => element.editing).every((row) => this.validateRow(row));
  }

  validateRow(row: SolicitanteRow) {
    return /^\d{1,9}$/.test(row.idSolicitante) &&
      /^(.+, .+)$/.test(row.solicitante) &&
      (row.solicitante !== null && row.solicitante.length > 0) &&
      (row.otrosDatos !== null && row.otrosDatos.length > 0) &&
      (row.balanza !== null && row.balanza.length > 0) &&
      ((row.telefono == null || row.telefono.length == 0) || /^\d+$/.test(row.telefono)) &&
      (row.almacenField.length > 0 && this.almacenesMap.get(row.almacenField) === row.almacen) &&
      !this.idExistsInTable(row);
  }

  idExistsInTable(row: SolicitanteRow) {
    if (row.estado) {
      return this.solicitantes.filter((element) => {
        return row != element &&
          row.idSolicitante == element.idSolicitante &&
          element.estado;

      }).length > 0;
    } else {
      return false;
    }
  }

  checkId(row: SolicitanteRow, control: MatFormField) {
    if (this.idExistsInTable(row)) {
      this.notificationService.open(ErrorCodes.DUPLICATE_SOLICITANTE, "warn");
      control._control.ngControl?.control?.setErrors({'id-already-exists':true});
    }
  }

  validateAlmacenSelection(almacenesMap: Map<string, Almacen>): (a: any) => boolean {
    return (almacen: string) => almacenesMap.get(almacen) !== undefined;
  }

  isDirtyAndNotAdd(element: SolicitanteRow, key: keyof Solicitante): boolean {
    return this.isDirty(element, key) && !element.add;
  }

  isDirty(element: SolicitanteRow, key: keyof Solicitante): boolean {
    if(key !== 'fecCreacion' && key !== 'usrModif' && key !== 'fecModif'){
      return element[key] !== element.originalSolicitante[key];
    }else{
      return false;
    }
  }

  private dirtyCheck(): boolean {
    return !this.solicitantes.every((row) => {
      return !row.editing;
    })
  }

  checkUsersExist(): Observable<{ name: string, exists: boolean }[]> {
    return forkJoin([
      of({name: '', exists: false}),
      ...this.solicitantes.filter((item) => item.add)
        .map((solicitante) => {
          return this.userService.exists(solicitante.solicitante).pipe(
            map((res) => {
              return {name: solicitante.solicitante, exists: res};
            }))
        })
    ]);
  }

  changeAlmacen(row: SolicitanteRow) {
    row.almacenField = row.almacenField.toUpperCase();
    const newAlmacen = this.almacenesMap.get(row.almacenField);
    if (newAlmacen) {
      row.changeAlmacen(newAlmacen);
    }
  }

  sortTableById() {
    this.solicitantes.sort((a, b) => {
      return b.id - a.id;
    });
  }
}
